/**
 * Phone Numbers Transformer
 * Transforms WooRank phone_numbers criteria data into React component-ready format
 */

import {
  getStatus,
  getIconType,
  getImportance,
  getSolvability,
} from '../../constants/seoMappings';

/**
 * Generates a scenario-based message for phone numbers
 * @param {Array} data - The phone numbers data array
 * @returns {string|null} - The appropriate message or null if data exists
 */
const generateMessage = (data = []) => {
  if (!data || data?.length === 0) {
    return 'No phone numbers were found.';
  }
  return null; // No message needed when data exists (table displayed)
};

/**
 * Transforms the phone_numbers criteria from WooRank API response
 * @param {Object} phoneNumbersCriteria - The phone_numbers criteria object from the API
 * @returns {Object} - Transformed phone numbers data for React components
 */
export const transformPhoneNumbers = (phoneNumbersCriteria) => {
  // Handle missing criteria entirely - use 'neutral' status (maps to 'info' icon)
  if (!phoneNumbersCriteria) {
    return {
      status: getStatus('neutral'),
      iconType: getIconType('neutral'),
      importance: { level: 0, text: '' },
      solvability: { level: 0, text: '' },
      isEmpty: true,
      message: 'No phone numbers were found.',
      data: null,
    };
  }

  const data = phoneNumbersCriteria?.data || [];
  const wooRankStatus = phoneNumbersCriteria?.status || 'neutral';
  const importance = phoneNumbersCriteria?.importance || 0;
  const solvability = phoneNumbersCriteria?.solvability || 0;

  const isEmpty = !data || data?.length === 0;

  return {
    status: getStatus(wooRankStatus),
    iconType: getIconType(wooRankStatus),
    importance: getImportance(importance),
    solvability: getSolvability(solvability),
    isEmpty,
    message: generateMessage(data),
    data: isEmpty ? null : {
      // API returns data as array of strings directly, not objects with 'value' property
      phoneNumbers: (data || []).map((item) => ({
        value: typeof item === 'string' ? item : (item?.value || ''),
      })),
    },
  };
};

export default transformPhoneNumbers;

